import { isOpeningStep, MAX_BETS, sequenceChannel, TrackerConfig } from './tracker-config';
import { clampFields, parseDelimitedRows } from './tracker-paste';
import {
  findDuplicatePerson,
  firstNameOf,
  fmtMoney,
  fold,
  FIELD_LIMITS,
  MAX_BET_NAME_LENGTH,
  MAX_BET_OFFER_LENGTH,
  MAX_NEXT_STEP_LENGTH,
  parseMoneyCents,
  personName,
  TrackerBet,
  TrackerPersonFields,
} from './tracker-people';
import { TrackerCloseAs } from './tracker-rollup';
import { parseSheetDate } from './tracker-import';

/**
 * The plan file: the tracker file the program's AI sessions write when a
 * participant's plan locks. One CSV with a few setting rows on top, then a
 * people table:
 *
 *   Revenue target,20000
 *   Close counts when,Collected
 *   Bet,Coaches,90-day coaching package
 *   Bet,Consultants,Pricing audit
 *
 *   Full Name,Email,Phone,Bet,First Touch Date,Notes
 *   Maya Thompson,maya@example.com,,Coaches,2026-11-23,Met at the summit
 *
 * Every setting is optional; a value in the file replaces the participant's
 * own, which the import panel shows for them to confirm before it saves.
 */
export const PLAN_SETTING_LABELS = {
  target: 'Revenue target',
  closeAs: 'Close counts when',
  bet: 'Bet',
} as const;

const PLAN_HEADERS = {
  firstName: 'first name',
  fullName: 'full name',
  email: 'email',
  phone: 'phone',
  bet: 'bet',
  firstTouch: 'first touch date',
  notes: 'notes',
};

type PlanColumn = keyof typeof PLAN_HEADERS;

/** A bet the file names: one the participant has, by name, or a new one. */
export type PlanBet = {
  name: string;
  offer: string;
  /** The participant's bet with this name. */
  existingId?: string;
  /** Set when the file changes that bet's offer: what it was. */
  offerWas?: string;
};

export type PlanSetup = {
  targetCents?: number;
  closeAs?: TrackerCloseAs;
  bets: PlanBet[];
};

export type PlanPerson = TrackerPersonFields & {
  /** The bet's name as the file's bet list or the participant's bets spell it. */
  betName?: string;
  /** The participant's bet, when the person goes under one they have. */
  betId?: string;
  nextStep: string;
  nextStepKey?: string;
  nextOn: string;
};

export type PlanImportResult = {
  setup: PlanSetup;
  people: PlanPerson[];
  /** Already in the tracker (same match as Add person), so skipped. */
  duplicates: PlanPerson[];
  noName: number;
  /** Setting problems: any one stops the whole import, as setup can't save half right. */
  setupProblems: string[];
  /** People rows left out, each named. */
  problems: string[];
  /** Set when the file can't be read at all; nothing imports. */
  fileError?: string;
};

/** The participant's setup the file is compared with. */
export type PlanParticipant = { closeAs?: TrackerCloseAs; targetCents?: number };

/** Case, spacing and dash style folded. */
function norm(text: string) {
  return text
    .trim()
    .toLowerCase()
    .replace(/[‐-―−]/g, '-')
    .replace(/\s+/g, ' ');
}

/** Whether a CSV is a plan file rather than a tab of the old RIS sheet. */
export function isPlanFile(text: string) {
  return parseDelimitedRows(text, ',').some((row) => {
    const first = norm(row[0] ?? '');
    return (
      first === norm(PLAN_SETTING_LABELS.target) ||
      first === norm(PLAN_SETTING_LABELS.closeAs) ||
      row.some((cell) => norm(cell) === PLAN_HEADERS.firstTouch)
    );
  });
}

function closeAsFrom(text: string): TrackerCloseAs | undefined {
  const value = norm(text);
  if (value === 'booked' || value === 'collected') {
    return value;
  }
}

/** Whether the participant's setup would end up with both a close choice and a target. */
export function planSetupChanges(setup: PlanSetup, participant: PlanParticipant) {
  const closeAs = setup.closeAs ?? participant.closeAs;
  const targetCents = setup.targetCents ?? participant.targetCents;
  return {
    closeAs,
    targetCents,
    scoreboard:
      (setup.closeAs !== undefined && setup.closeAs !== participant.closeAs) ||
      (setup.targetCents !== undefined && setup.targetCents !== participant.targetCents),
  };
}

export function hasPlanSetup(setup: PlanSetup) {
  return setup.targetCents !== undefined || setup.closeAs !== undefined || setup.bets.length > 0;
}

/**
 * Reads a plan file into setup values and people. Setting problems stop the
 * import; a people row with a problem is left out and named, and the rest
 * import. People already in the tracker are skipped, so importing the same
 * file twice adds no one. Each person starts on the course's opening touch,
 * due on their First Touch Date.
 */
export function parsePlanImport(opts: {
  config: TrackerConfig;
  csv: string;
  existing: Array<TrackerPersonFields & { betId?: string }>;
  /** The participant's bets, on a course with bets on. */
  bets: TrackerBet[];
  participant: PlanParticipant;
}): PlanImportResult {
  const { config } = opts;
  const result: PlanImportResult = {
    setup: { bets: [] },
    people: [],
    duplicates: [],
    noName: 0,
    setupProblems: [],
    problems: [],
  };
  const channel = sequenceChannel(config);
  if (!channel || config.scoreboard.kind !== 'revenue') {
    result.fileError =
      'A plan file needs a revenue sprint with an outreach sequence. This course isn’t one.';
    return result;
  }
  const opening = config.sequence.find(isOpeningStep);

  const rows = parseDelimitedRows(opts.csv, ',');
  const headerIndex = rows.findIndex(
    (row) =>
      row.some((cell) => norm(cell) === PLAN_HEADERS.firstTouch) &&
      row.some((cell) => [PLAN_HEADERS.fullName, PLAN_HEADERS.firstName].includes(norm(cell))),
  );
  if (headerIndex < 0) {
    result.fileError =
      'This doesn’t look like a plan file. It needs a row of column names with Full Name and First Touch Date.';
    return result;
  }

  const setup = result.setup;
  const setupProblem = (text: string) => result.setupProblems.push(text);
  for (let i = 0; i < headerIndex; i++) {
    const cells = rows[i].map((c) => c.trim());
    if (cells.every((c) => !c)) {
      continue;
    }
    const rowNumber = i + 1;
    const key = norm(cells[0] ?? '');
    const value = cells[1] ?? '';
    if (key === norm(PLAN_SETTING_LABELS.target)) {
      const cents = parseMoneyCents(value);
      if (setup.targetCents !== undefined) {
        setupProblem(`Row ${rowNumber}: Revenue target is in the file twice. Keep one.`);
      } else if (cents === undefined) {
        setupProblem(`Row ${rowNumber}, Revenue target “${value}”: use numbers only, like 20000.`);
      } else {
        setup.targetCents = cents;
      }
    } else if (key === norm(PLAN_SETTING_LABELS.closeAs)) {
      const closeAs = closeAsFrom(value);
      if (setup.closeAs !== undefined) {
        setupProblem(`Row ${rowNumber}: Close counts when is in the file twice. Keep one.`);
      } else if (!closeAs) {
        setupProblem(`Row ${rowNumber}, Close counts when “${value}”: use Booked or Collected.`);
      } else {
        setup.closeAs = closeAs;
      }
    } else if (key === norm(PLAN_SETTING_LABELS.bet)) {
      const name = value;
      const offer = cells[2] ?? '';
      if (!config.bets) {
        setupProblem(`Row ${rowNumber}: this course doesn’t use bets. Remove the Bet rows.`);
      } else if (!name) {
        setupProblem(`Row ${rowNumber}: the bet has no name. Name it, like “Coaches”.`);
      } else if (name.length > MAX_BET_NAME_LENGTH) {
        setupProblem(
          `Row ${rowNumber}, bet “${name}”: use a name of up to ${MAX_BET_NAME_LENGTH} characters.`,
        );
      } else if (offer.length > MAX_BET_OFFER_LENGTH) {
        setupProblem(
          `Row ${rowNumber}, bet “${name}”: use an offer of up to ${MAX_BET_OFFER_LENGTH} characters.`,
        );
      } else if (setup.bets.some((b) => fold(b.name) === fold(name))) {
        setupProblem(`Row ${rowNumber}: the bet “${name}” is in the file twice. Keep one.`);
      } else {
        const existing = opts.bets.find((b) => fold(b.name) === fold(name));
        const bet: PlanBet = {
          name: existing?.name ?? name,
          offer: offer || (existing?.offer ?? ''),
        };
        if (existing) {
          bet.existingId = existing.id;
          if (offer && offer !== existing.offer) {
            bet.offerWas = existing.offer;
          }
        }
        setup.bets.push(bet);
      }
    } else {
      setupProblem(
        `Row ${rowNumber}, “${cells[0]}”: not a setting. Use Revenue target, Close counts when or Bet.`,
      );
    }
  }
  const added = setup.bets.filter((b) => !b.existingId).length;
  if (added && opts.bets.length + added > MAX_BETS) {
    setupProblem(
      `Up to ${MAX_BETS} bets. You have ${opts.bets.length} already and the file adds ${added}. Remove ${
        opts.bets.length + added - MAX_BETS === 1 ? 'one' : 'some'
      } from the file or from your tracker.`,
    );
  }
  if (setup.closeAs !== undefined || setup.targetCents !== undefined) {
    const resolved = planSetupChanges(setup, opts.participant);
    if (!resolved.closeAs) {
      setupProblem('Add a Close counts when row, with Booked or Collected.');
    }
    if (resolved.targetCents === undefined) {
      setupProblem('Add a Revenue target row, like “Revenue target,20000”.');
    }
  }

  const header = rows[headerIndex].map(norm);
  const index = {} as Record<PlanColumn, number>;
  for (const key of Object.keys(PLAN_HEADERS) as PlanColumn[]) {
    index[key] = header.indexOf(PLAN_HEADERS[key]);
  }
  const allBets = [
    ...setup.bets,
    ...opts.bets
      .filter((b) => !setup.bets.some((s) => s.existingId === b.id))
      .map((b) => ({ name: b.name, offer: b.offer, existingId: b.id })),
  ];
  if (config.bets && index.bet < 0) {
    result.fileError = 'Add a Bet column, so each person goes under one of your bets.';
    return result;
  }

  const seen: Array<TrackerPersonFields & { betId?: string }> = [...opts.existing];
  const fromFile = new Set<TrackerPersonFields>();
  for (let i = headerIndex + 1; i < rows.length; i++) {
    const row = rows[i];
    const rowNumber = i + 1;
    const get = (key: PlanColumn) => (index[key] >= 0 ? (row[index[key]] ?? '').trim() : '');
    if (row.every((c) => !c.trim())) {
      continue;
    }
    const fullName = get('fullName');
    const firstName = get('firstName') || firstNameOf(fullName);
    if (!firstName && !fullName) {
      result.noName++;
      continue;
    }
    const problems: string[] = [];
    let bet: PlanBet | undefined;
    if (config.bets) {
      const betText = fold(get('bet'));
      bet = betText
        ? allBets.find(
            (b) => fold(b.name) === betText || (!!fold(b.offer) && fold(b.offer) === betText),
          )
        : undefined;
      if (!betText) {
        problems.push(`Row ${rowNumber}, Bet is empty: name one of your bets, like “Coaches”.`);
      } else if (!bet) {
        problems.push(`Row ${rowNumber}, Bet “${get('bet')}” isn’t one of your bets.`);
      }
    }
    const touchText = get('firstTouch');
    const firstTouchOn = parseSheetDate(touchText);
    if (!touchText) {
      problems.push(
        `Row ${rowNumber}, First Touch Date is empty: add the day you plan to send the first message.`,
      );
    } else if (!firstTouchOn) {
      problems.push(
        `Row ${rowNumber}, First Touch Date “${touchText}”: use a date like 2026-11-23.`,
      );
    }
    if (problems.length || !firstTouchOn) {
      result.problems.push(...problems);
      continue;
    }
    const fields = clampFields({
      firstName,
      fullName,
      offer: '',
      email: get('email'),
      phone: get('phone'),
      segment: '',
      tier: '',
      note: get('notes'),
    });
    if (!fields.firstName) {
      fields.firstName = firstNameOf(fields.fullName).slice(0, FIELD_LIMITS.firstName);
    }
    const person: PlanPerson = {
      ...fields,
      betName: bet?.name,
      betId: bet?.existingId,
      nextStep: (opening?.name ?? '').slice(0, MAX_NEXT_STEP_LENGTH),
      nextStepKey: opening?.key,
      nextOn: firstTouchOn,
    };
    const slot = {
      ...person,
      betId: bet ? (bet.existingId ?? `new:${fold(bet.name)}`) : undefined,
    };
    const duplicate = findDuplicatePerson(slot, seen);
    if (duplicate && fromFile.has(duplicate)) {
      result.problems.push(
        `Row ${rowNumber}: ${personName(person)} is in the file twice${
          bet ? ` under “${bet.name}”` : ''
        }. Keep one row.`,
      );
      continue;
    }
    if (duplicate) {
      result.duplicates.push(person);
      continue;
    }
    seen.push(slot);
    fromFile.add(slot);
    result.people.push(person);
  }
  return result;
}

/** The setup lines the import panel asks the participant to confirm. */
export function planSetupLines(setup: PlanSetup, participant: PlanParticipant) {
  const lines: Array<{ label: string; value: string; was?: string }> = [];
  if (setup.targetCents !== undefined) {
    lines.push({
      label: 'Revenue target',
      value: fmtMoney(setup.targetCents),
      was:
        participant.targetCents !== undefined && participant.targetCents !== setup.targetCents
          ? fmtMoney(participant.targetCents)
          : undefined,
    });
  }
  if (setup.closeAs !== undefined) {
    const word = (c: TrackerCloseAs) => (c === 'booked' ? 'Booked' : 'Collected');
    lines.push({
      label: 'A close counts when the money is',
      value: word(setup.closeAs),
      was:
        participant.closeAs && participant.closeAs !== setup.closeAs
          ? word(participant.closeAs)
          : undefined,
    });
  }
  for (const bet of setup.bets) {
    lines.push({
      label: bet.existingId ? `Bet “${bet.name}”` : `New bet “${bet.name}”`,
      value: bet.offer || 'No offer',
      was: bet.offerWas !== undefined ? bet.offerWas || 'No offer' : undefined,
    });
  }
  return lines;
}
